import { differenceInCalendarDays } from 'date-fns';
import { dateToLocalStr, parseDateStr } from '@/shared/lib/date-utils';
import type { Cadence } from '@/modules/scorecards/types';

/**
 * Shared period utility functions for metrics data tables.
 *
 * Periods are configurable per workspace via PeriodConfig:
 *   - startOfWeek: 0 (Sun) … 6 (Sat). Default 1 (Mon).
 *   - firstWeekRule: 'touching' (week containing quarter's first day is W1)
 *                  | 'full' (first week whose start ≥ quarter's first day is W1).
 *
 * Period key formats:
 *   - Weekly:  YYYY-Q#-W#  (e.g. "2026-Q2-W3")
 *   - Monthly: YYYY-M#     (e.g. "2026-M4")
 *   - Quarterly: YYYY-Q#   (e.g. "2026-Q3")
 *   - Yearly: YYYY         (e.g. "2026")
 */

export interface PeriodDescriptor {
  key: string;
  label: string;
  subLabel?: string;
  fullKey: string;
  periodStart: string; // YYYY-MM-DD
  periodEnd: string;   // YYYY-MM-DD
}

export type StartOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export type FirstWeekRule = 'touching' | 'full';
export type MeetingDataPeriod = 'previous' | 'current';

export interface PeriodConfig {
  startOfWeek: StartOfWeek;
  firstWeekRule: FirstWeekRule;
  today?: string;
}

export const DEFAULT_PERIOD_CONFIG: PeriodConfig = {
  startOfWeek: 1,
  firstWeekRule: 'touching',
};

const MONTH_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ─── Date helpers ──────────────────────────────────────────

/** Start of the configured-week-start for the week containing `date`. */
export function getWeekStart(date: Date, startOfWeek: StartOfWeek = 1): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0=Sun..6=Sat
  const diff = (day - startOfWeek + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function formatWeekRange(start: Date, end: Date): string {
  if (start.getMonth() === end.getMonth()) {
    return `${MONTH_SHORT[start.getMonth()]} ${start.getDate()}–${end.getDate()}`;
  }
  return `${MONTH_SHORT[start.getMonth()]} ${start.getDate()} – ${MONTH_SHORT[end.getMonth()]} ${end.getDate()}`;
}

/** "Mon, Jan 12" */
export function formatWeekStartLabel(d: Date): string {
  return `${DAY_SHORT[d.getDay()]}, ${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

/**
 * Calendar days from today to the last day of the quarter, floored at zero.
 *
 * Exclusive of today, matching every other countdown in the app — a milestone's
 * urgency, a rock's "Nd left", a due date. On the quarter's last day this is 0,
 * the same way a deadline reads "due today" rather than "one day left".
 *
 * `todayStr` is passed in rather than read from the clock: quarter arithmetic
 * belongs on the workspace's configured timezone, the same one due dates and
 * overdue detection use.
 */
export function daysLeftInQuarter(year: number, quarter: number, todayStr: string): number {
  return Math.max(0, differenceInCalendarDays(
    parseDateStr(getQuarterEndDateStr(year, quarter)),
    parseDateStr(todayStr),
  ));
}

/** Calendar end-of-quarter date as YYYY-MM-DD, e.g. 2026 Q1 → "2026-03-31". */
export function getQuarterEndDateStr(year: number, quarter: number): string {
  const d = new Date(year, quarter * 3, 0);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Calendar start-of-quarter date as YYYY-MM-DD, e.g. 2026 Q2 → "2026-04-01".
 * Calendar, not config-aligned: this answers "has this quarter begun", which is
 * a question about the calendar, not about where the workspace starts its weeks.
 */
export function getQuarterStartDateStr(year: number, quarter: number): string {
  const m = String((quarter - 1) * 3 + 1).padStart(2, '0');
  return `${year}-${m}-01`;
}

/** First (week-start) date of W1 of the given quarter, per config. */
function week1Start(year: number, quarter: number, cfg: PeriodConfig): Date {
  const qFirstDay = new Date(year, (quarter - 1) * 3, 1);
  const containing = getWeekStart(qFirstDay, cfg.startOfWeek);
  if (cfg.firstWeekRule === 'full' && containing < qFirstDay) {
    // Bump to the next week to ensure W1 is fully in the quarter.
    const next = new Date(containing);
    next.setDate(next.getDate() + 7);
    return next;
  }
  return containing;
}

/**
 * Week of `quarter` containing `date`, clamped to the quarter's thirteen.
 *
 * Counted in calendar days, not elapsed milliseconds: a DST transition makes a
 * week 167 or 169 hours long, so dividing a duration puts the boundary day on
 * the wrong side of the rollover twice a year.
 */
function weekOfQuarter(date: Date, year: number, quarter: number, cfg: PeriodConfig): number {
  const days = differenceInCalendarDays(date, week1Start(year, quarter, cfg));
  return Math.max(1, Math.min(13, Math.floor(days / 7) + 1));
}

// ─── Key formatting / parsing ──────────────────────────────

export function weeklyKey(year: number, quarter: number, week: number): string {
  return `${year}-Q${quarter}-W${week}`;
}

export function monthlyKey(year: number, month: number): string {
  return `${year}-M${month}`;
}

export function quarterlyKey(year: number, quarter: number): string {
  return `${year}-Q${quarter}`;
}

export function yearlyKey(year: number): string {
  return String(year);
}

export type ParsedPeriodKey =
  | { cadence: 'weekly'; year: number; quarter: number; week: number }
  | { cadence: 'monthly'; year: number; month: number }
  | { cadence: 'quarterly'; year: number; quarter: number }
  | { cadence: 'yearly'; year: number };

/** Parse a strict period key and identify its cadence and components. */
export function parsePeriodKey(key: string): ParsedPeriodKey | null {
  const w = key.match(/^(\d{4})-Q([1-4])-W(\d{1,2})$/);
  if (w && +w[3] >= 1 && +w[3] <= 13) return { cadence: 'weekly', year: +w[1], quarter: +w[2], week: +w[3] };
  const m = key.match(/^(\d{4})-M(\d{1,2})$/);
  if (m && +m[2] >= 1 && +m[2] <= 12) return { cadence: 'monthly', year: +m[1], month: +m[2] };
  const q = key.match(/^(\d{4})-Q([1-4])$/);
  if (q) return { cadence: 'quarterly', year: +q[1], quarter: +q[2] };
  const y = key.match(/^(\d{4})$/);
  if (y) return { cadence: 'yearly', year: +y[1] };
  return null;
}

export function periodKeyLabel(key: string): string {
  const parsed = parsePeriodKey(key);
  if (!parsed) return key;
  if (parsed.cadence === 'weekly') return `Q${parsed.quarter} ${parsed.year}, week ${parsed.week}`;
  if (parsed.cadence === 'monthly') {
    return new Date(parsed.year, parsed.month - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
  }
  if (parsed.cadence === 'quarterly') return `Q${parsed.quarter} ${parsed.year}`;
  return String(parsed.year);
}

/** Compute periodStart/periodEnd for a key under the given config. Returns null if unparseable. */
export function periodBoundsFromKey(key: string, cfg: PeriodConfig = DEFAULT_PERIOD_CONFIG): { periodStart: string; periodEnd: string } | null {
  const parsed = parsePeriodKey(key);
  if (!parsed) return null;
  if (parsed.cadence === 'yearly') {
    return { periodStart: `${parsed.year}-01-01`, periodEnd: `${parsed.year}-12-31` };
  }
  if (parsed.cadence === 'quarterly') {
    return {
      periodStart: getQuarterStartDateStr(parsed.year, parsed.quarter),
      periodEnd: getQuarterEndDateStr(parsed.year, parsed.quarter),
    };
  }
  if (parsed.cadence === 'monthly') {
    const { year, month } = parsed;
    const lastDay = new Date(year, month, 0).getDate();
    return {
      periodStart: `${year}-${String(month).padStart(2, '0')}-01`,
      periodEnd: `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
    };
  }
  const { year, quarter, week } = parsed;
  const start = week1Start(year, quarter, cfg);
  start.setDate(start.getDate() + (week - 1) * 7);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return { periodStart: dateToLocalStr(start), periodEnd: dateToLocalStr(end) };
}

// ─── Weekly periods ────────────────────────────────────────

export function buildWeeklyCurrentPeriods(year: number, quarter: number, cfg: PeriodConfig = DEFAULT_PERIOD_CONFIG): PeriodDescriptor[] {
  const w1 = week1Start(year, quarter, cfg);
  return Array.from({ length: 13 }, (_, i) => {
    const wk = i + 1;
    const start = new Date(w1);
    start.setDate(start.getDate() + i * 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const fullKey = weeklyKey(year, quarter, wk);
    return {
      key: fullKey,
      label: `Wk ${wk}`,
      subLabel: formatWeekRange(start, end),
      fullKey,
      periodStart: dateToLocalStr(start),
      periodEnd: dateToLocalStr(end),
    };
  });
}

export function buildWeeklyTrailingPeriods(year: number, quarter: number, todayStr: string, cfg: PeriodConfig = DEFAULT_PERIOD_CONFIG): PeriodDescriptor[] {
  const now = parseDateStr(todayStr);
  const w1 = week1Start(year, quarter, cfg);
  const diffMs = now.getTime() - w1.getTime();
  const currentWeek = Math.max(1, Math.min(13, Math.ceil(diffMs / (7 * 24 * 60 * 60 * 1000))));

  const collected: PeriodDescriptor[] = [];
  let remainingWeeks = 13;
  let curYear = year;
  let curQuarter = quarter;
  let curWeek = currentWeek;

  while (remainingWeeks > 0) {
    const qW1 = week1Start(curYear, curQuarter, cfg);
    for (let w = curWeek; w >= 1 && remainingWeeks > 0; w--) {
      const start = new Date(qW1);
      start.setDate(start.getDate() + (w - 1) * 7);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const fullKey = weeklyKey(curYear, curQuarter, w);
      collected.push({
        key: fullKey,
        label: `Q${curQuarter} W${w}`,
        subLabel: formatWeekRange(start, end),
        fullKey,
        periodStart: dateToLocalStr(start),
        periodEnd: dateToLocalStr(end),
      });
      remainingWeeks--;
    }
    curQuarter--;
    if (curQuarter < 1) { curQuarter = 4; curYear--; }
    curWeek = 13;
  }
  return collected.reverse();
}

// ─── Monthly periods ───────────────────────────────────────

export function cyclePeriods(
  cadence: Cadence,
  year: number,
  quarter: number,
  cfg: PeriodConfig = DEFAULT_PERIOD_CONFIG,
): PeriodDescriptor[] {
  if (cadence === 'weekly') return buildWeeklyCurrentPeriods(year, quarter, cfg);
  if (cadence === 'quarterly') return [{
    key: quarterlyKey(year, quarter), label: `Q${quarter}`, fullKey: quarterlyKey(year, quarter),
    periodStart: getQuarterStartDateStr(year, quarter), periodEnd: getQuarterEndDateStr(year, quarter),
  }];
  if (cadence === 'yearly') return [{
    key: yearlyKey(year), label: String(year), fullKey: yearlyKey(year),
    periodStart: `${year}-01-01`, periodEnd: `${year}-12-31`,
  }];
  const first = (quarter - 1) * 3;
  return buildMonthlyCurrentPeriods(year).slice(first, first + 3);
}

export function buildMonthlyCurrentPeriods(year: number): PeriodDescriptor[] {
  return Array.from({ length: 12 }, (_, i) => {
    const m = i + 1;
    const fullKey = monthlyKey(year, m);
    const lastDay = new Date(year, m, 0).getDate();
    return {
      key: fullKey,
      label: MONTH_ABBR[i],
      fullKey,
      periodStart: `${year}-${String(m).padStart(2, '0')}-01`,
      periodEnd: `${year}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
    };
  });
}

export function buildMonthlyTrailingPeriods(todayStr: string): PeriodDescriptor[] {
  const now = parseDateStr(todayStr);
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const periods: PeriodDescriptor[] = [];
  for (let i = 11; i >= 0; i--) {
    let m = currentMonth - i;
    let y = currentYear;
    while (m <= 0) { m += 12; y--; }
    const fullKey = monthlyKey(y, m);
    const lastDay = new Date(y, m, 0).getDate();
    const label = y === currentYear ? MONTH_ABBR[m - 1] : `${MONTH_SHORT[m - 1]} ${y}`;
    periods.push({
      key: fullKey,
      label,
      fullKey,
      periodStart: `${y}-${String(m).padStart(2, '0')}-01`,
      periodEnd: `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
    });
  }
  return periods;
}

/** 13 weekly periods ending at (and including) the week identified by anchorKey. */
export function buildWeeklyWindowFromEnd(
  anchorKey: string,
  cfg: PeriodConfig = DEFAULT_PERIOD_CONFIG,
): PeriodDescriptor[] {
  const anchor = parsePeriodKey(anchorKey);
  if (!anchor || anchor.cadence !== 'weekly') return [];

  const collected: PeriodDescriptor[] = [];
  let curYear = anchor.year;
  let curQuarter = anchor.quarter;
  let curWeek = anchor.week;

  for (let i = 0; i < 13; i++) {
    const qW1 = week1Start(curYear, curQuarter, cfg);
    const start = new Date(qW1);
    start.setDate(start.getDate() + (curWeek - 1) * 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const fullKey = weeklyKey(curYear, curQuarter, curWeek);
    collected.push({
      key: fullKey,
      label: `Q${curQuarter} W${curWeek}`,
      subLabel: formatWeekRange(start, end),
      fullKey,
      periodStart: dateToLocalStr(start),
      periodEnd: dateToLocalStr(end),
    });
    curWeek--;
    if (curWeek < 1) {
      curQuarter--;
      if (curQuarter < 1) { curQuarter = 4; curYear--; }
      curWeek = 13;
    }
  }
  return collected.reverse();
}

/** 12 monthly periods ending at (and including) the month identified by anchorKey. */
export function buildMonthlyWindowFromEnd(anchorKey: string): PeriodDescriptor[] {
  const anchor = parsePeriodKey(anchorKey);
  if (!anchor || anchor.cadence !== 'monthly') return [];

  const collected: PeriodDescriptor[] = [];
  let curYear = anchor.year;
  let curMonth = anchor.month;

  for (let i = 0; i < 12; i++) {
    const fullKey = monthlyKey(curYear, curMonth);
    const lastDay = new Date(curYear, curMonth, 0).getDate();
    collected.push({
      key: fullKey,
      label: MONTH_ABBR[curMonth - 1],
      subLabel: curYear !== anchor.year ? String(curYear) : undefined,
      fullKey,
      periodStart: `${curYear}-${String(curMonth).padStart(2, '0')}-01`,
      periodEnd: `${curYear}-${String(curMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
    });
    curMonth--;
    if (curMonth < 1) { curMonth = 12; curYear--; }
  }
  return collected.reverse();
}

/** 12 calendar quarters ending at the selected quarter. */
export function buildQuarterlyWindowFromEnd(anchorKey: string): PeriodDescriptor[] {
  const anchor = parsePeriodKey(anchorKey);
  if (!anchor || anchor.cadence !== 'quarterly') return [];
  const periods: PeriodDescriptor[] = [];
  let year = anchor.year;
  let quarter = anchor.quarter;
  for (let i = 0; i < 12; i++) {
    const fullKey = quarterlyKey(year, quarter);
    periods.push({
      key: fullKey,
      label: `Q${quarter}`,
      subLabel: year !== anchor.year ? String(year) : undefined,
      fullKey,
      periodStart: getQuarterStartDateStr(year, quarter),
      periodEnd: getQuarterEndDateStr(year, quarter),
    });
    quarter--;
    if (quarter < 1) { quarter = 4; year--; }
  }
  return periods.reverse();
}

/** 10 calendar years ending at the selected year. */
export function buildYearlyWindowFromEnd(anchorKey: string): PeriodDescriptor[] {
  const anchor = parsePeriodKey(anchorKey);
  if (!anchor || anchor.cadence !== 'yearly') return [];
  return Array.from({ length: 10 }, (_, index) => {
    const year = anchor.year - 9 + index;
    const fullKey = yearlyKey(year);
    return {
      key: fullKey,
      label: fullKey,
      fullKey,
      periodStart: `${year}-01-01`,
      periodEnd: `${year}-12-31`,
    };
  });
}

export function buildPeriodWindowFromEnd(cadence: Cadence, anchorKey: string, cfg: PeriodConfig = DEFAULT_PERIOD_CONFIG): PeriodDescriptor[] {
  if (cadence === 'weekly') return buildWeeklyWindowFromEnd(anchorKey, cfg);
  if (cadence === 'monthly') return buildMonthlyWindowFromEnd(anchorKey);
  if (cadence === 'quarterly') return buildQuarterlyWindowFromEnd(anchorKey);
  return buildYearlyWindowFromEnd(anchorKey);
}

/** Immediately following period key. Weekly: wraps Q's W13 → next Q's W1. Monthly: wraps Dec → next Jan. */
export function getNextPeriodKey(key: string): string | null {
  const parsed = parsePeriodKey(key);
  if (!parsed) return null;
  if (parsed.cadence === 'yearly') return yearlyKey(parsed.year + 1);
  if (parsed.cadence === 'quarterly') {
    return parsed.quarter === 4 ? quarterlyKey(parsed.year + 1, 1) : quarterlyKey(parsed.year, parsed.quarter + 1);
  }
  if (parsed.cadence === 'monthly') {
    if (parsed.month === 12) return monthlyKey(parsed.year + 1, 1);
    return monthlyKey(parsed.year, parsed.month + 1);
  }
  if (parsed.week < 13) return weeklyKey(parsed.year, parsed.quarter, parsed.week + 1);
  let nextQ = parsed.quarter + 1;
  let nextY = parsed.year;
  if (nextQ > 4) { nextQ = 1; nextY++; }
  return weeklyKey(nextY, nextQ, 1);
}

/** Compare two period keys of the same cadence. Returns negative if a < b, 0 if equal, positive if a > b. */
export function comparePeriodKeys(a: string, b: string): number {
  const pa = parsePeriodKey(a) as any;
  const pb = parsePeriodKey(b) as any;
  if (!pa || !pb || pa.cadence !== pb.cadence) return 0;
  if (pa.cadence === 'yearly') return pa.year - pb.year;
  if (pa.cadence === 'quarterly') {
    if (pa.year !== pb.year) return pa.year - pb.year;
    return pa.quarter - pb.quarter;
  }
  if (pa.cadence === 'monthly') {
    if (pa.year !== pb.year) return pa.year - pb.year;
    return pa.month - pb.month;
  }
  if (pa.year !== pb.year) return pa.year - pb.year;
  if (pa.quarter !== pb.quarter) return pa.quarter - pb.quarter;
  return pa.week - pb.week;
}

// ─── Current period key (based on today's date) ────────────

export function getCurrentPeriodKey(cadence: Cadence, todayStr: string, cfg: PeriodConfig = DEFAULT_PERIOD_CONFIG): string {
  const now = parseDateStr(todayStr);
  if (cadence === 'yearly') return yearlyKey(now.getFullYear());
  if (cadence === 'quarterly') return quarterlyKey(now.getFullYear(), Math.floor(now.getMonth() / 3) + 1);
  if (cadence === 'monthly') return monthlyKey(now.getFullYear(), now.getMonth() + 1);
  const month = now.getMonth();
  const quarter = Math.floor(month / 3) + 1;
  const year = now.getFullYear();
  return weeklyKey(year, quarter, weekOfQuarter(now, year, quarter, cfg));
}

export function getCurrentWeekOfQuarter(year: number, quarter: number, todayStr: string, cfg: PeriodConfig = DEFAULT_PERIOD_CONFIG): number {
  return weekOfQuarter(parseDateStr(todayStr), year, quarter, cfg);
}

/** Period the next meeting will discuss, given the workspace's data-period setting. */
export function getNextInputPeriodKey(
  cadence: Cadence,
  todayStr: string,
  cfg: PeriodConfig = DEFAULT_PERIOD_CONFIG,
  meetingDataPeriod: MeetingDataPeriod = 'previous',
): string {
  const now = parseDateStr(todayStr);
  if (cadence === 'yearly') {
    return yearlyKey(meetingDataPeriod === 'current' ? now.getFullYear() : now.getFullYear() - 1);
  }
  if (cadence === 'quarterly') {
    const quarter = Math.floor(now.getMonth() / 3) + 1;
    if (meetingDataPeriod === 'current') return quarterlyKey(now.getFullYear(), quarter);
    return quarter === 1 ? quarterlyKey(now.getFullYear() - 1, 4) : quarterlyKey(now.getFullYear(), quarter - 1);
  }
  if (cadence === 'monthly') {
    if (meetingDataPeriod === 'current') return monthlyKey(now.getFullYear(), now.getMonth() + 1);
    // 'previous': the prior calendar month
    const m = now.getMonth(); // 0-indexed; m=0 means we want last year's December
    if (m === 0) return monthlyKey(now.getFullYear() - 1, 12);
    return monthlyKey(now.getFullYear(), m); // m here is 1-based for previous
  }
  // weekly
  const month = now.getMonth();
  const quarter = Math.floor(month / 3) + 1;
  const year = now.getFullYear();
  const cw = getCurrentWeekOfQuarter(year, quarter, todayStr, cfg);
  if (meetingDataPeriod === 'current') return weeklyKey(year, quarter, cw);
  // previous
  if (cw > 1) return weeklyKey(year, quarter, cw - 1);
  // wrap to prior quarter's W13
  let prevQ = quarter - 1;
  let prevY = year;
  if (prevQ < 1) { prevQ = 4; prevY--; }
  return weeklyKey(prevY, prevQ, 13);
}

/** Immediately preceding period key. Weekly: wraps W1 → prior Q's W13. Monthly: wraps Jan → prior Dec. */
export function getPreviousPeriodKey(key: string): string | null {
  const parsed = parsePeriodKey(key);
  if (!parsed) return null;
  if (parsed.cadence === 'yearly') return yearlyKey(parsed.year - 1);
  if (parsed.cadence === 'quarterly') {
    return parsed.quarter === 1 ? quarterlyKey(parsed.year - 1, 4) : quarterlyKey(parsed.year, parsed.quarter - 1);
  }
  if (parsed.cadence === 'monthly') {
    if (parsed.month === 1) return monthlyKey(parsed.year - 1, 12);
    return monthlyKey(parsed.year, parsed.month - 1);
  }
  if (parsed.week > 1) return weeklyKey(parsed.year, parsed.quarter, parsed.week - 1);
  let prevQ = parsed.quarter - 1;
  let prevY = parsed.year;
  if (prevQ < 1) { prevQ = 4; prevY--; }
  return weeklyKey(prevY, prevQ, 13);
}

/** Comparable earlier period: same week in the prior quarter, or same month/quarter in the prior year. */
export function getPreviousEquivalentPeriodKey(key: string): string | null {
  const parsed = parsePeriodKey(key);
  if (!parsed) return null;
  if (parsed.cadence === 'yearly') return null;
  if (parsed.cadence === 'quarterly') return quarterlyKey(parsed.year - 1, parsed.quarter);
  if (parsed.cadence === 'monthly') {
    return monthlyKey(parsed.year - 1, parsed.month);
  }
  let prevQ = parsed.quarter - 1;
  let prevY = parsed.year;
  if (prevQ < 1) { prevQ = 4; prevY--; }
  return weeklyKey(prevY, prevQ, parsed.week);
}

/** Return an array of `count` period keys ending at `key` (inclusive, newest first). */
export function getPreviousPeriodKeys(key: string, count: number): string[] {
  const keys: string[] = [key];
  let current = key;
  for (let i = 1; i < count; i++) {
    const prev = getPreviousPeriodKey(current);
    if (!prev) break;
    keys.push(prev);
    current = prev;
  }
  return keys;
}
